import { ExternalLink, Code, Github, Globe, Monitor, ArrowRight } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { trackPortfolioView, trackPortfolioClick } from '../utils/analytics';

import tenniScraperPhoto from '../assets/img/Tenniscraper.webp';
import jpcarsPhoto from '../assets/img/jpcars.webp';
import argomoobel from '../assets/img/argomoobel.webp';
import kiaCarnivalPhoto from '../assets/img/kiacarnival.webp';
import ferryPhoto from '../assets/img/ferry-poller.webp';
import beepBoopPhoto from '../assets/img/beepboop.webp';

const Portfolio = () => {
    const { t } = useTranslation();


    const projects = [
        {
            id: 1,
            title: t('portfolio.projects.tenniScraper.title'),
            description: t('portfolio.projects.tenniScraper.description'),
            image: tenniScraperPhoto,
            technologies: ["Vanilla JS", "Java Spring", "PostgreSQL"],
            categoryKey: 'softwareDevelopment',
            icon: Code,
            githubUrl: "https://github.com/MathiasRanna/TenniScraper"
        },
        {
            id: 2,
            title: t('portfolio.projects.ferry.title'),
            description: t('portfolio.projects.ferry.description'),
            image: ferryPhoto,
            technologies: ["Laravel", "React", "MariaDB"],
            categoryKey: 'softwareDevelopment',
            icon: Code,
            liveUrl: "https://ferry.finfratech.ee",
            githubUrl: "https://github.com/MathiasRanna/PraamidPoller"
        },
        {
            id: 3,
            title: t('portfolio.projects.beepBoop.title'),
            description: t('portfolio.projects.beepBoop.description'),
            image: beepBoopPhoto,
            technologies: ["WooCommerce", "WordPress", "PHP"],
            categoryKey: 'wordpress',
            icon: Monitor,
            liveUrl: "https://beepboop.ee",
        },
        {
            id: 4,
            title: t('portfolio.projects.kiaCarnival.title'),
            description: t('portfolio.projects.kiaCarnival.description'),
            image: kiaCarnivalPhoto,
            technologies: ["PHP", "Tailwind CSS"],
            categoryKey: 'softwareDevelopment',
            icon: Code,
            liveUrl: "https://kiacarnival.eu",
        },
        {
            id: 5,
            title: t('portfolio.projects.jpCars.title'),
            description: t('portfolio.projects.jpCars.description'),
            image: jpcarsPhoto,
            technologies: ["PHP", "Tailwind CSS"],
            categoryKey: 'softwareDevelopment',
            icon: Code,
            liveUrl: "https://jpcars.ee",
        },
        {
            id: 6,
            title: t('portfolio.projects.argomoobel.title'),
            description: t('portfolio.projects.argomoobel.description'),
            image: argomoobel,
            technologies: ["WordPress", "Elementor", "PHP"],
            categoryKey: 'wordpress',
            icon: Globe,
            liveUrl: "https://argomoobel.ee",
        },
    ];

    return (
        <section id="portfolio" className="py-24 bg-slate-950" aria-labelledby="portfolio-heading">
            <div className="container mx-auto px-6">

                {/* Header */}
                <div className="text-center mb-12">
                    <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
                        {t('portfolio.eyebrow')}
                    </span>
                    <h2 id="portfolio-heading" className="text-4xl md:text-5xl font-bold text-white mb-4">
                        {t('portfolio.title')}
                    </h2>
                </div>

                {/* Projects grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {projects.map((project) => (
                        <article
                            key={project.id}
                            className="group bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 hover:-translate-y-1"
                            onMouseEnter={() => trackPortfolioView(project.title, project.categoryKey)}
                        >
                            {/* Image */}
                            <div className="relative overflow-hidden h-48">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                {/* Hover overlay with links */}
                                <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                                    {project.liveUrl && (
                                        <a
                                            href={project.liveUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer"
                                            onClick={() => trackPortfolioClick(project.title, 'live', project.liveUrl!)}
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            Live
                                        </a>
                                    )}
                                    {project.githubUrl && (
                                        <a
                                            href={project.githubUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer"
                                            onClick={() => trackPortfolioClick(project.title, 'github', project.githubUrl!)}
                                        >
                                            <Github className="w-4 h-4" />
                                            GitHub
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <project.icon className="w-4 h-4 text-blue-400" aria-hidden="true" />
                                    <span className="text-xs text-blue-400 font-medium uppercase tracking-wide">
                                        {t(`portfolio.categories.${project.categoryKey}`)}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-200">
                                    {project.title}
                                </h3>
                                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                                    {project.description}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {project.technologies.map((tech) => (
                                        <span
                                            key={tech}
                                            className="px-2.5 py-1 bg-slate-800 border border-slate-700 text-gray-400 text-xs rounded-lg"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* More work banner */}
                <div className="relative overflow-hidden bg-gradient-to-br from-blue-600/10 to-purple-600/10 border border-blue-500/20 rounded-2xl px-8 py-10 text-center">
                    {/* Background glow */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(59,130,246,0.08),transparent_70%)]" aria-hidden="true" />

                    <div className="relative">
                        <h3 className="text-xl font-bold text-white mb-3">
                            {t('portfolio.more.title')}
                        </h3>
                        <p className="text-gray-400 leading-relaxed mb-6 max-w-2xl mx-auto">
                            {t('portfolio.more.description')}
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            {(t('portfolio.more.tags', { returnObjects: true }) as string[]).map((tag) => (
                                <span key={tag} className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-full text-sm">
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <button
                            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 cursor-pointer hover:shadow-lg hover:shadow-blue-500/25 group"
                        >
                            {t('portfolio.more.cta')}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </button>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default Portfolio;
